import { create } from 'zustand';
import type { User } from '../types';
import { apiService } from '../services/apiService';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadProfile: () => Promise<void>;
  clearError: () => void;
  initializeAuth: () => Promise<void>; // Initialize auth state on app load
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      // apiService.login returns AuthResponse with token and user
      const authResponse = await apiService.login({ email, password });
      
      console.log('🔍 Full Auth Response:', authResponse);
      console.log('🔍 Auth Response Keys:', Object.keys(authResponse));
      
      // Extract user data from the response
      let userData = authResponse.user || authResponse;
      
      // TEMPORARY FIX: For admin users, add admin permissions manually
      if (userData.email === 'admin@suhfreitas.com.br' || 
          userData.Email === 'admin@suhfreitas.com.br' ||
          userData.subscription_tier === 'enterprise' ||
          userData.SubscriptionTier === 'enterprise') {
        userData = {
          ...userData,
          Roles: ['admin', 'user', 'enterprise'],
          roles: ['admin', 'user', 'enterprise'],
          customClaims: {
            admin: true,
            roles: ['admin', 'user', 'enterprise'],
            tier: 'enterprise'
          }
        };
      }
      
      console.log('👤 Final User Data to Store:', userData);
      console.log('👤 User Data Keys:', Object.keys(userData));
      
      // Store FULL user data in localStorage for persistence
      localStorage.setItem('user_data', JSON.stringify(userData));
      
      set({
        user: userData,
        isAuthenticated: true,
        isLoading: false,
      });

      // Check if there's a redirect URL saved from token expiration
      const redirectUrl = localStorage.getItem('redirect_after_login');
      if (redirectUrl) {
        localStorage.removeItem('redirect_after_login');
        // Use window.location.href to navigate to the saved URL
        window.location.href = redirectUrl;
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Login failed',
        isLoading: false,
      });
      throw error;
    }
  },

  register: async (name, email, password) => {
    set({ isLoading: true, error: null });
    try {
      const userProfile = await apiService.register({ name, email, password });
      set({
        user: userProfile,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "An unknown error occurred";
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  logout: async () => {
    await apiService.logout();
    // Clear all stored data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    set({
      user: null,
      isAuthenticated: false,
      error: null,
    });
  },

  loadProfile: async () => {
    set({ isLoading: true, error: null });
    try {
      const user = await apiService.getProfile();
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load profile',
        isLoading: false,
        isAuthenticated: false,
        user: null,
      });
    }
  },

  clearError: () => set({ error: null }),

  initializeAuth: async () => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    
    console.log('🔄 Initialize Auth:', { token: !!token, userData: !!userData });
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        console.log('✅ User loaded from localStorage:', user);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        console.log('❌ Error parsing user data:', error);
        // Invalid stored data, clear everything
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        set({
          isLoading: false,
          isAuthenticated: false,
          user: null,
        });
      }
    } else {
      console.log('⚠️ No token or user data found');
      set({
        isLoading: false,
        isAuthenticated: false,
        user: null,
      });
    }
  },
}));